import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

const serviceData = [
  {
    id: 'card-engine',
    number: '01',
    title: '3Dカードデータ解析',
    titleEn: '3D Card Data Analytics',
    description: '独自開発のAIエンジンにより、3Dカードの微細なパターンや特徴を高精度で解析。真贋判定、グレーディング、市場価値予測まで一貫したデータ分析を提供します。',
    features: ['高精度AIパターン認識', 'リアルタイム真贋判定', '市場価値動向分析', 'グレーディング自動化'],
    imageUrl: 'https://readdy.ai/api/search-image?query=Abstract%20warm%20golden%20data%20visualization%20with%20floating%20card%20shaped%20geometric%20patterns%20and%20network%20connections%20minimalist%20Japanese%20tech%20aesthetic%20soft%20gradients%20elegant%20modern%20design%20clean%20composition%20depth%20of%20field&width=800&height=600&seq=service-card-engine&orientation=landscape',
  },
  {
    id: 'bigdata',
    number: '02',
    title: 'ビッグデータ分析基盤',
    titleEn: 'Big Data Analytics Platform',
    description: '膨大なカードデータを統合管理し、統計分析から予測モデリングまでをシームレスに実行。データドリブンな意思決定を支援するエンタープライズ向け分析基盤です。',
    features: ['ペタバイト級データ処理', '予測分析モデリング', 'リアルタイムダッシュボード', 'カスタムレポート生成'],
    imageUrl: 'https://readdy.ai/api/search-image?query=Abstract%20warm%20golden%20server%20room%20data%20center%20with%20flowing%20data%20streams%20and%20dashboard%20visualizations%20minimalist%20Japanese%20tech%20aesthetic%20soft%20gradients%20elegant%20modern%20design%20clean%20composition&width=800&height=600&seq=service-bigdata&orientation=landscape',
  },
  {
    id: 'datavision',
    number: '03',
    title: 'AI可視化ソリューション',
    titleEn: 'AI Visualization Solution',
    description: '複雑なデータを直感的に理解できる3Dビジュアライゼーション。インタラクティブな操作でデータの深層に潜むインサイトを引き出し、プレゼンテーションやレポート作成を強力に支援します。',
    features: ['3Dインタラクティブ可視化', 'AR/VR表示対応', 'カスタムダッシュボード', 'API連携機能'],
    imageUrl: 'https://readdy.ai/api/search-image?query=Abstract%20warm%20golden%203d%20holographic%20data%20visualization%20with%20floating%20charts%20and%20graphs%20minimalist%20Japanese%20tech%20aesthetic%20soft%20gradients%20elegant%20modern%20design%20clean%20composition%20depth%20of%20field&width=800&height=600&seq=service-datavision&orientation=landscape',
  },
];

function ServiceSection({ service, index }: { service: typeof serviceData[0]; index: number }) {
  const isOdd = index % 2 === 1;
  return (
    <section className={`py-20 md:py-28 ${isOdd ? 'bg-background-100' : ''}`}>
      <div className="max-w-[1280px] mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 md:gap-20 items-center">
          <div className={isOdd ? 'lg:order-2' : ''}>
            <div className="aspect-[4/3] rounded-2xl overflow-hidden">
              <img
                src={service.imageUrl}
                alt={service.title}
                className="w-full h-full object-cover"
              />
            </div>
          </div>
          <div className={isOdd ? 'lg:order-1' : ''}>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-primary-100/70 text-primary-600 font-heading text-lg font-bold">
                {service.number}
              </div>
              <span className="text-xs font-medium tracking-[0.15em] text-primary-600 bg-primary-100/70 px-3 py-1 rounded-full">
                {service.titleEn}
              </span>
            </div>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-6 tracking-wide">
              {service.title}
            </h2>
            <p className="text-sm text-foreground-600 leading-relaxed mb-8">
              {service.description}
            </p>
            <div className="space-y-3 mb-8">
              {service.features.map((feature) => (
                <div key={feature} className="flex items-center gap-3">
                  <div className="w-6 h-6 flex items-center justify-center rounded-full bg-primary-100/70 text-primary-500 shrink-0">
                    <i className="ri-check-line text-sm"></i>
                  </div>
                  <span className="text-sm text-foreground-700">{feature}</span>
                </div>
              ))}
            </div>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 px-7 py-3 bg-foreground-900 text-background-50 font-medium text-sm tracking-wider rounded-full hover:bg-foreground-800 transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              詳細を問い合わせる
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function Services() {
  return (
    <div className="min-h-screen">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-32 pb-16 md:pt-40 md:pb-24 bg-background-950 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Abstract%20flowing%20golden%20amber%20data%20streams%20with%20geometric%20patterns%20in%20dark%20space%20minimalist%20Japanese%20tech%20aesthetic%20warm%20gradients%20elegant%20atmosphere%20particle%20effects%20cinematic%20quality&width=1800&height=1000&seq=services-hero-abstract&orientation=landscape"
            alt="事業紹介 背景"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="absolute inset-0 bg-foreground-950/75"></div>
        <div className="relative z-10 max-w-[1280px] mx-auto px-6 md:px-10">
          <div className="max-w-3xl">
            <span className="inline-block text-xs font-medium tracking-[0.15em] text-primary-300 bg-primary-500/20 px-4 py-1.5 rounded-full mb-6">
              SERVICES
            </span>
            <h1 className="font-heading text-3xl md:text-5xl font-bold text-foreground-50 leading-tight mb-6 tracking-wide">
              事業紹介
            </h1>
            <p className="text-foreground-300 text-sm md:text-base leading-relaxed max-w-2xl">
              3つのコアサービスで、データ活用の全領域を包括的にカバーします。
              解析から可視化まで、一気通貫のソリューションをご提供します。
            </p>
          </div>
        </div>
      </section>

      {serviceData.map((service, index) => (
        <ServiceSection key={service.id} service={service} index={index} />
      ))}

      {/* CTA */}
      <section className="py-20 md:py-28">
        <div className="max-w-[1280px] mx-auto px-6 md:px-10">
          <div className="relative rounded-2xl overflow-hidden">
            <div className="absolute inset-0">
              <img
                src="https://readdy.ai/api/search-image?query=Warm%20abstract%20data%20center%20server%20room%20with%20amber%20lighting%20geometric%20patterns%20minimalist%20Japanese%20tech%20aesthetic%20soft%20gradients%20elegant%20atmosphere%20cinematic%20quality%20film%20grain&width=1800&height=800&seq=services-cta-bg&orientation=landscape"
                alt="CTA 背景"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="absolute inset-0 bg-foreground-950/75"></div>
            <div className="relative z-10 py-16 md:py-24 px-8 md:px-16 text-center">
              <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-50 mb-3 tracking-wide">
                企業のデータ資産を
              </h2>
              <p className="font-heading text-2xl md:text-4xl text-foreground-300 italic mb-2">
                最大限に
              </p>
              <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-50 mb-8 tracking-wide">
                引き出すソリューション
              </h2>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 px-8 py-3.5 bg-primary-500 text-background-50 font-medium text-sm tracking-wider rounded-full hover:bg-primary-600 transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                詳細を問い合わせる
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}